import axios from 'axios';

/**
 * Axios client for the Laravel Sanctum SPA backend.
 *
 * Auth model (cookie/session):
 *   1. GET {API_URL}/sanctum/csrf-cookie  -> sets XSRF-TOKEN cookie
 *   2. Axios automatically reads XSRF-TOKEN and sends it as X-XSRF-TOKEN
 *      (xsrfCookieName / xsrfHeaderName below) on subsequent mutating requests.
 *   3. withCredentials ensures the session + xsrf cookies travel with every call.
 */

// Resolve the API origin so the app works on localhost AND on a LAN IP/domain
// without rebuilding. If VITE_API_URL is set to a non-localhost URL, it wins
// (explicit prod/staging override). Otherwise — or when the page is opened on a
// LAN host while the env still points at localhost — derive the API host from the
// address the browser used, on port 8000.
// Backend port: the launcher (start-zonta.bat) auto-picks a free port and passes
// it as VITE_API_PORT. Falls back to 8001 when run manually.
const API_PORT = import.meta.env.VITE_API_PORT || 8000;
function resolveApiOrigin() {
  const envUrl = import.meta.env.VITE_API_URL;
  // Honor an explicit non-local override (e.g. production domain); ignore any
  // stale localhost default so we always target the current host on API_PORT.
  if (envUrl && !/localhost|127\.0\.0\.1/.test(envUrl)) return envUrl;
  if (typeof window === 'undefined') return `http://localhost:${API_PORT}`;
  return `${window.location.protocol}//${window.location.hostname}:${API_PORT}`;
}

const API_ORIGIN = resolveApiOrigin();

export const api = axios.create({
  baseURL: `${API_ORIGIN}/api`,
  withCredentials: true,
  withXSRFToken: true, // axios >=1.6 needs this explicitly for cross-site
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

let csrfReady = false;

/** Fetch the CSRF cookie once before the first mutating request. */
export async function ensureCsrf() {
  if (csrfReady) return;
  await axios.get(`${API_ORIGIN}/sanctum/csrf-cookie`, { withCredentials: true });
  csrfReady = true;
}

/** Reset CSRF state (e.g. after logout) so it re-primes on next mutation. */
export function resetCsrf() {
  csrfReady = false;
}

// Prime CSRF automatically before any non-GET request.
api.interceptors.request.use(async (config) => {
  const method = (config.method || 'get').toLowerCase();
  if (method !== 'get' && method !== 'head') {
    await ensureCsrf();
  }
  return config;
});

/**
 * Normalize Laravel error payloads into a consistent shape:
 *   { status, message, errors: { field: [msg] } }
 */
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error.response?.status;
    const data = error.response?.data;
    const normalized = {
      status,
      message: data?.message || error.message || 'Something went wrong.',
      errors: data?.errors || {},
      isAuthError: status === 401 || status === 419,
      isForbidden: status === 403,
    };
    return Promise.reject(normalized);
  }
);

/** Build an absolute URL for a backend-stored asset path. */
export function assetUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  // Root-relative paths are frontend public assets (e.g. /img/...) — serve as-is.
  if (path.startsWith('/')) return path;
  return `${API_ORIGIN}/storage/${path.replace(/^\/?storage\/?/, '')}`;
}

export { API_ORIGIN };
